import { Licenses } from "common/components/subscriptions";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import useCampaignActions from "../../hooks/useCampaignActions";
import useCampaignValidation from "../../hooks/useCampaignValidation";
import AssignSeats from "../AssignSeats";
import CampaignScheduling from "../CampaignScheduling";
import CampaignNameEdit from "./CampaignNameEdit";
import DeleteCampaign from "./DeleteCampaign";
import DuplicateCampaign from "./DuplicateCampaign";

import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { CampaignActionType, DetailedCampaign } from "../../types";

interface CampaignActionsDialogProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  actionType: CampaignActionType;
  campaign: DetailedCampaign | Campaign;
}

export default function CampaignActionsDialog({
  isOpen,
  setIsOpen,
  actionType,
  campaign,
}: CampaignActionsDialogProps) {
  const { id, owners, outreach_type: outreachType, name, type } = campaign;

  const isDesktop = useTwBreakpoint("lg");

  function onClose() {
    setIsOpen(false);
  }

  const { updateCampaignState, onUnscheduleCampaign, isActionPending } =
    useCampaignActions(campaign, onClose);

  const { workerAccountsWithoutSeat, missingSeatsCount } =
    useCampaignValidation(owners);

  if (actionType === "license") {
    return (
      <Licenses
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        newSeatsCount={missingSeatsCount}
        accountIds={workerAccountsWithoutSeat.map(
          ({ id: accountId }) => accountId,
        )}
      />
    );
  }

  const buttonSize = isDesktop ? "md" : "lg";

  let mainContent: ReactNode;
  if (actionType === "delete") {
    mainContent = (
      <DeleteCampaign
        campaignId={id}
        campaignName={name}
        onClose={() => setIsOpen(false)}
        onCancel={() => setIsOpen(false)}
      />
    );
  } else if (actionType === "duplicate") {
    mainContent = (
      <DuplicateCampaign
        campaignId={id}
        accountIds={owners}
        name={name}
        isDrip={outreachType === "drip"}
        type={type}
        onCancel={onClose}
      />
    );
  } else if (actionType === "editName") {
    mainContent = (
      <CampaignNameEdit campaignName={name} campaignId={id} onClose={onClose} />
    );
  } else if (
    (actionType === "start" || actionType === "schedule") &&
    !!workerAccountsWithoutSeat.length
  ) {
    mainContent = (
      <AssignSeats
        accounts={workerAccountsWithoutSeat}
        onClose={onClose}
        onSuccess={onClose}
      />
    );
  } else if (actionType === "schedule") {
    mainContent = (
      <CampaignScheduling
        campaignId={id}
        onCancel={onClose}
        onSuccess={onClose}
      />
    );
  } else {
    const Title = isDesktop ? DialogTitle : DrawerTitle;
    const Description = isDesktop ? DialogDescription : DrawerDescription;

    let onClick: () => void;

    if (actionType === "unschedule") {
      onClick = onUnscheduleCampaign;
    } else {
      onClick = updateCampaignState;
    }

    mainContent = (
      <>
        <Title className="first-letter:uppercase">{actionType} campaign</Title>

        <Description className="mb-8">
          Are you sure you want to {actionType} campaign &quot;
          {name}
          &quot;?
        </Description>

        <div className="flex items-center justify-between gap-x-2.5">
          <Button
            variant="secondary-black"
            onClick={onClose}
            className="lg:flex-1"
            size={buttonSize}
            disabled={isActionPending}
          >
            Cancel
          </Button>

          <Button
            size={buttonSize}
            onClick={onClick}
            className="lg:flex-1 capitalize"
            isLoading={isActionPending}
          >
            {actionType}
          </Button>
        </div>
      </>
    );
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Content
        className={
          actionType === "schedule" || actionType === "duplicate"
            ? "lg:w-[540px]"
            : "lg:w-[448px]"
        }
      >
        {mainContent}
      </Content>
    </Component>
  );
}
